/** Web-side scene registry. Mirrors src/scenes/registry.ts on the Worker. */
export type SceneSlug = 'pelican-test' | 'endless-runner' | 'watermelon-test'

export interface SceneInfo {
  slug: SceneSlug
  /** Page root, e.g. /lab/pelican-test */
  path: string
  /** 'The Pelican Test' */
  name: string
  /** Unit noun for copy: 'pelican' / 'game' */
  unit: string
  unitPlural: string
  /** Kind options shown in the run panel: [kind, label] */
  kinds: Array<[string, string]>
  /**
   * How a result is shown: 'svg' drawings, playable 'game' iframes, or
   * browser-rendered 'frames' (cards show frame 1, the result page plays the page).
   */
  media: 'svg' | 'game' | 'frames'
  /** Leaderboard / profile label for ok_rate. */
  okLabel: string
  /** Whether the scene has an animated kind whose motion is measured. */
  tracksMotion: boolean
  /** Whether runs may change the subject (remixes). False: one fixed prompt. */
  remixable: boolean
  /** Card eyebrow for results on the classic subject. */
  classicTitle: string
}

export const SCENES: Record<SceneSlug, SceneInfo> = {
  'pelican-test': {
    slug: 'pelican-test',
    path: '/lab/pelican-test',
    name: 'The Pelican Test',
    unit: 'pelican',
    unitPlural: 'pelicans',
    kinds: [['static', 'Classic SVG'], ['animated', 'Animated SVG']],
    media: 'svg',
    okLabel: 'Draws something',
    tracksMotion: true,
    remixable: true,
    classicTitle: 'Pelican riding a bicycle',
  },
  'endless-runner': {
    slug: 'endless-runner',
    path: '/lab/endless-runner',
    name: 'Endless Runner',
    unit: 'game',
    unitPlural: 'games',
    kinds: [['game', 'Game']],
    media: 'game',
    okLabel: 'Builds a game',
    tracksMotion: false,
    remixable: true,
    classicTitle: 'Neon city runner',
  },
  'watermelon-test': {
    slug: 'watermelon-test',
    path: '/lab/watermelon-test',
    name: 'The Watermelon Test',
    unit: 'toy',
    unitPlural: 'toys',
    kinds: [['toy', 'Toy']],
    media: 'frames',
    okLabel: 'Runs',
    tracksMotion: false,
    remixable: false,
    classicTitle: 'Watermelon rubber-band toy',
  },
}

/** Scenes in menu order. */
export const SCENE_SLUGS = Object.keys(SCENES) as SceneSlug[]

/** The scene a /lab/<scene>/… path belongs to; null outside every scene. */
export function sceneOfPath(pathname: string): SceneSlug | null {
  return SCENE_SLUGS.find((slug) => pathname === SCENES[slug].path || pathname.startsWith(`${SCENES[slug].path}/`)) ?? null
}

/**
 * The scene's name after "the" in running copy: "Run the Watermelon Test",
 * "Run the Endless Runner". Names that carry their own article ("The Pelican Test")
 * lose it so the sentence never reads "the The …".
 */
export function theSceneName(scene: SceneSlug): string {
  return `the ${SCENES[scene].name.replace(/^the\s+/i, '')}`
}

export const sceneApi = (scene: SceneSlug): string => `/lab/api/${scene}`
export const sceneRunApi = (scene: SceneSlug): string => `/lab/api/run/${scene}`
export const sceneMineApi = (scene: SceneSlug): string => `/lab/api/run/${scene}/mine`
